import React, { useState, useRef, useEffect } from "react";
import UserDropDown from "./UserDropDown";
import {
  ThunderboltOutlined,
  RobotOutlined,
  ExperimentOutlined,
  PartitionOutlined,
  MessageOutlined,
} from "@ant-design/icons";

const NAVBAR_HEIGHT = 56;

// Primary nav items shown directly in the navbar
const PRIMARY_KEYS = ["explore", "gallery", "community"];

const CREATE_ITEMS = [
  {
    key: "zen",
    path: "/zen",
    icon: <ThunderboltOutlined />,
    title: "Zen",
    description: "Quick, focused AI generation",
  },
  {
    key: "agent",
    path: "/agent",
    icon: <RobotOutlined />,
    title: "AI Agent",
    description: "Chat-driven creative assistant",
  },
  {
    key: "playground",
    path: "/playground",
    icon: <ExperimentOutlined />,
    title: "Playground",
    description: "Advanced model parameters",
  },
  {
    key: "workflowPlayground",
    path: "/workflow-playground",
    icon: <PartitionOutlined />,
    title: "Workflows",
    description: "Multi-step automated pipelines",
  },
  {
    key: "llmPlayground",
    path: "/llm-playground",
    icon: <MessageOutlined />,
    title: "LLM Playground",
    description: "Chat with LLM models",
  },
];

export function AppNavbar({
  routes = [],
  pathname = "/",
  onNavigate = () => {},
  balance = 0,
  onTopUp = () => {},
  onHelp = () => {},
}) {
  const [hoveredKey, setHoveredKey] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const createRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!createOpen) return;
    const handler = (e) => {
      if (createRef.current && !createRef.current.contains(e.target)) {
        setCreateOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [createOpen]);

  const primaryRoutes = routes.filter(
    (r) => r.sidebar && PRIMARY_KEYS.includes(r.key),
  );

  // Is any create tool active?
  const isCreateActive = CREATE_ITEMS.some(
    (item) =>
      pathname === item.path || pathname.startsWith(item.path + "/"),
  );

  return (
    <nav
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        height: NAVBAR_HEIGHT,
        display: "flex",
        alignItems: "center",
        background: 'rgba(5,5,5,0.97)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        padding: "0 24px",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* Logo */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          flexShrink: 0,
          marginRight: 32,
        }}
        onClick={() => onNavigate("/explore")}
      >
        <img
          style={{ height: 30, objectFit: "contain" }}
          src="/uro_logo_white.png"
          alt="URO"
        />
      </div>

      {/* Center nav */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {/* Create dropdown */}
        <div ref={createRef} style={{ position: "relative" }}>
          <div
            onClick={() => setCreateOpen((v) => !v)}
            onMouseEnter={() => setHoveredKey("__create")}
            onMouseLeave={() => setHoveredKey(null)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              fontSize: 13,
              fontWeight: isCreateActive || createOpen ? 600 : 500,
              letterSpacing: "0.01em",
              color:
                isCreateActive || createOpen
                  ? "#FFFFFF"
                  : hoveredKey === "__create"
                    ? "rgba(255,255,255,0.75)"
                    : 'rgba(255,255,255,0.45)',
              borderBottom: isCreateActive
                ? '2px solid #FF7E67'
                : "2px solid transparent",
              marginBottom: -1,
              transition: "color 150ms ease",
            }}
          >
            <span>Create</span>
            <svg
              width="10"
              height="6"
              viewBox="0 0 10 6"
              fill="none"
              style={{
                transition: "transform 150ms ease",
                transform: createOpen ? "rotate(180deg)" : "none",
              }}
            >
              <path
                d="M1 1L5 5L9 1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {/* Create dropdown menu */}
          {createOpen && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 8px)",
                left: 0,
                minWidth: 260,
                background: '#0d0d0d',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 12,
                boxShadow: "0 12px 40px rgba(0,0,0,0.6)",
                padding: "6px",
                zIndex: 1001,
              }}
            >
              {CREATE_ITEMS.map((item) => {
                const active =
                  pathname === item.path ||
                  pathname.startsWith(item.path + "/");
                return (
                  <div
                    key={item.key}
                    onClick={() => {
                      onNavigate(item.path);
                      setCreateOpen(false);
                    }}
                    onMouseEnter={() => setHoveredKey(item.key)}
                    onMouseLeave={() => setHoveredKey(null)}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      padding: "10px 12px",
                      borderRadius: 8,
                      cursor: "pointer",
                      borderLeft: active
                        ? '2px solid #FF7E67'
                        : hoveredKey === item.key
                          ? '2px solid rgba(255,126,103,0.4)'
                          : "2px solid transparent",
                      background:
                        active
                          ? 'rgba(255,126,103,0.1)'
                          : hoveredKey === item.key
                            ? 'rgba(255,255,255,0.04)'
                            : "transparent",
                      transition: "all 120ms ease",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 16,
                        color: active ? '#FF7E67' : 'rgba(255,255,255,0.4)',
                        marginTop: 1,
                      }}
                    >
                      {item.icon}
                    </span>
                    <div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: active ? 600 : 500,
                          color: active ? "#FFFFFF" : 'rgba(255,255,255,0.8)',
                          marginBottom: 2,
                        }}
                      >
                        {item.title}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'rgba(255,255,255,0.35)',
                          lineHeight: 1.3,
                        }}
                      >
                        {item.description}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Primary nav items */}
        {primaryRoutes.map((route) => {
          const active =
            pathname === route.path ||
            pathname.startsWith(route.path + "/");
          const hovered = hoveredKey === route.key;
          return (
            <div
              key={route.key}
              onClick={() => onNavigate(route.path)}
              onMouseEnter={() => setHoveredKey(route.key)}
              onMouseLeave={() => setHoveredKey(null)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                cursor: "pointer",
                whiteSpace: "nowrap",
                fontSize: 13,
                fontWeight: active ? 600 : 500,
                letterSpacing: "0.01em",
                color: active
                  ? "#FFFFFF"
                  : hovered
                    ? "rgba(255,255,255,0.75)"
                    : 'rgba(255,255,255,0.45)',
              borderBottom: active
                ? '2px solid #FF7E67'
                : "2px solid transparent",
                marginBottom: -1,
                transition: "color 150ms ease",
              }}
            >
              <span
                style={{
                  fontSize: 14,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {route.icon}
              </span>
              <span>{route.title}</span>
            </div>
          );
        })}
      </div>

      {/* Right section */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexShrink: 0,
        }}
      >
        {/* Balance — clickable, goes to top up */}
        <div
          onClick={onTopUp}
          onMouseEnter={() => setHoveredKey("__balance")}
          onMouseLeave={() => setHoveredKey(null)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            borderRadius: 8,
            cursor: "pointer",
            background:
              hoveredKey === "__balance"
                ? 'rgba(255,255,255,0.07)'
                : 'rgba(255,255,255,0.04)',
            transition: "background 150ms ease",
          }}
          title="Click to top up"
        >
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: 3,
              background:
                balance < 0
                  ? "#EF4444"
                  : balance < 1
                    ? "#F59E0B"
                    : '#4ade80',
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#FFFFFF',
            }}
          >
            {Number.isFinite(Number(balance))
              ? Number(balance) >= 1
                ? Number(balance).toFixed(1)
                : Number(balance).toFixed(4)
              : "—"}
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: 'rgba(255,255,255,0.4)',
            }}
          >
            URO
          </span>
        </div>

        {/* Divider */}
        <div
          style={{
            width: 1,
            height: 20,
            background: 'rgba(255,255,255,0.1)',
          }}
        />

        {/* Help */}
        <div
          onClick={onHelp}
          onMouseEnter={() => setHoveredKey("__help")}
          onMouseLeave={() => setHoveredKey(null)}
          style={{
            width: 28,
            height: 28,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 6,
            cursor: "pointer",
            color:
              hoveredKey === "__help" ? '#FF7E67' : 'rgba(255,255,255,0.4)',
            background:
              hoveredKey === "__help" ? 'rgba(255,126,103,0.1)' : "transparent",
            fontSize: 13,
            fontWeight: 500,
            transition: "all 150ms ease",
          }}
          title="Need help?"
        >
          ?
        </div>

        {/* User dropdown */}
        <UserDropDown onNavigate={onNavigate} />
      </div>
    </nav>
  );
}

export { NAVBAR_HEIGHT };
export default AppNavbar;
